import type { DataItemId } from '@pn/core/domain/data';
import type { WorkspaceItem } from '@pn/core/domain/workspace';
import { createSlice } from '@reduxjs/toolkit';
import { defaultWorkspace } from './defaultWorkspaces';
import { moduleItemIds, pnWorkspaceItems } from './pnWorkspaceItems';
import {
  add,
  addLayer,
  addToWorkspace,
  create,
  disableBubbleMapping,
  enableBubbleMapping,
  markAsError,
  markAsLoaded,
  markAsProcessed,
  markAsRendered,
  markAsUnfinalized,
  overrideLayers,
  overwrite,
  recalculateAllPages,
  recolorPNSymbolLayers,
  remove,
  removeAreasFromWorkspace,
  removeFromWorkspace,
  removeLayer,
  removeStackOriginatedItems,
  resetInternalState,
  revertLayersToOriginal,
  revisualize,
  select,
  setCheckedIds,
  setWorkspace,
  share,
  unshare,
  update,
  updateAllLayerStyles,
  updateClickedIds,
  updateCounts,
  updateFilterPropertyOptions,
  updateFiltering,
  updateInvalidDataItemIds,
  updateIsFetching,
  updateIsFetchingStack,
  updateIsStackAccessDenied,
  updateLayerPattern,
  updateLayerStyle,
  updateName,
  updateNumberOfElements,
  updatePage,
  updateQueryProperty,
  updateScopeIds,
  updateSorts,
  updateTableDataItems,
  updateVisibility,
} from './workspaceSliceReducers';

export type State = {
  isFetching: boolean;
  isFetchingStack: boolean;
  isStackAccessDenied: boolean;
  allWorkspaceItems: Record<WorkspaceItem['id'], WorkspaceItem>;
  stackWorkspaceItems: Record<WorkspaceItem['id'], WorkspaceItem>;
  idsInWorkspace: WorkspaceItem['id'][];
  workspaceItemIdSelected: WorkspaceItem['id'] | undefined;
  invalidDataItemIds: DataItemId[];
};

const initialState: State = {
  isFetching: false,
  isFetchingStack: false,
  isStackAccessDenied: false,
  allWorkspaceItems: pnWorkspaceItems,
  stackWorkspaceItems: {},
  idsInWorkspace: [...moduleItemIds, ...defaultWorkspace],
  workspaceItemIdSelected: undefined,
  invalidDataItemIds: [],
};

export const workspaceSlice = createSlice({
  name: 'workspace',
  initialState,
  reducers: {
    add,
    create,
    update,
    overwrite,
    remove,
    removeStackOriginatedItems,
    removeAreasFromWorkspace,
    share,
    unshare,
    updateInvalidDataItemIds,
    updateIsFetching,
    updateIsFetchingStack,
    updateIsStackAccessDenied,
    updateQueryProperty,
    updateFilterPropertyOptions,
    addLayer,
    removeLayer,
    updateLayerStyle,
    updateAllLayerStyles,
    updateLayerPattern,
    overrideLayers,
    revertLayersToOriginal,
    recolorPNSymbolLayers,
    enableBubbleMapping,
    disableBubbleMapping,
    updateNumberOfElements,
    updateName,
    addToWorkspace,
    removeFromWorkspace,
    setWorkspace,
    select,
    updateCounts,
    updateVisibility,
    updateTableDataItems,
    markAsLoaded,
    markAsProcessed,
    markAsUnfinalized,
    markAsRendered,
    markAsError,
    revisualize,
    resetInternalState,
    updateSorts,
    updateFiltering,
    updatePage,
    recalculateAllPages,
    setCheckedIds,
    updateScopeIds,
    updateClickedIds,
  },
});
